import { logger } from '../../../utils/logger'
import { getJsonSessionItem, setJsonSessionItem } from '../../../utils/session-storage'
import type { ServiceHandler } from './service-registry'
import { getSessionStorageCustomQuestionsKey } from './setCustomQuestionAnswer'
import type {
  DonorQuestionAnswers,
  ClearDonorQuestionAnswerRequest,
  ClearDonorQuestionAnswerResponse,
} from '@custom-embedded-code-sdk/methods/donor-questions'

/**
 * CLEAR_CUSTOM_QUESTION_ANSWER service handler
 *
 * Handles requests to remove the answer for a single Custom Question.
 * Removes the answer from session storage under the key `custom_questions_{campaignId}`.
 */
export const clearCustomQuestionAnswer: ServiceHandler<
  ClearDonorQuestionAnswerRequest,
  ClearDonorQuestionAnswerResponse
> = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
  pageConfig,
}) => {
  const campaignId = pageConfig.campaignId

  try {
    if (!campaignId) {
      throw new Error('CustomEmbeddedCode:clearCustomQuestionAnswer: Campaign ID not available')
    }

    // TODO: CL-51093 improve payload validation with Yup/Zod library
    if (!payload || typeof payload !== 'string') {
      sendErrorResponse(iframeId, messageId, 'Question ID is required and must be a string')
      return
    }

    const questionId = payload
    const storageKey = getSessionStorageCustomQuestionsKey(campaignId)
    const existingAnswers = getJsonSessionItem<DonorQuestionAnswers>(storageKey) || {}

    delete existingAnswers[questionId]
    setJsonSessionItem(storageKey, existingAnswers)

    logger(
      'info',
      'CustomEmbeddedCode:clearCustomQuestionAnswer: Custom Question answer cleared successfully',
      {
        context: { iframeId, messageId, campaignId, questionId, storageKey },
      },
    )

    sendSuccessResponse(iframeId, messageId, { success: true })
  } catch (error) {
    logger(
      'error',
      new Error(
        'CustomEmbeddedCode:clearCustomQuestionAnswer: Failed to clear Custom Question answer',
        {
          cause: error,
        },
      ),
      {
        context: { iframeId, messageId, campaignId },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to clear Custom Question answer')
  }
}
